<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Add Event
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="4">
              <v-text-field
                v-model="form.name"
                outlined
                label="Event Name"
                prepend-inner-icon="mdi-calendar-text"
                :rules="[(v) => !!v || 'Event name is required']"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-select
                v-model="form.type"
                :items="eventTypes"
                outlined
                label="Event Type"
                prepend-inner-icon="mdi-calendar-star"
                :rules="[(v) => !!v || 'Event type is required']"
                @change="onEventTypeChange"
              ></v-select>
            </v-col>
            <v-col sm="4" v-if="form.type && availableRewardOptions.length > 0">
              <v-select
                v-model="selectedRewardOption"
                :items="availableRewardOptions"
                outlined
                label="Reward Option"
                prepend-inner-icon="mdi-trophy"
                :rules="[(v) => !!v || 'Reward option is required']"
              ></v-select>
            </v-col>
            <v-col sm="4">
              <v-menu
                v-model="menu_start"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.start_date"
                    label="Start Date"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    readonly
                    :rules="[(v) => !!v || 'Start date is required']"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.start_date"
                  @input="menu_start = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="4">
              <v-menu
                v-model="menu_end"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="290px"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="form.end_date"
                    label="End Date"
                    prepend-inner-icon="mdi-calendar"
                    v-bind="attrs"
                    v-on="on"
                    outlined
                    readonly
                    :rules="[(v) => !!v || 'End date is required']"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="form.end_date"
                  :min="form.start_date"
                  @input="menu_end = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col sm="12" v-if="selectedRewardOption">
              <v-divider></v-divider>
              <h3 class="mb-3">Event Reward Configuration</h3>
            </v-col>

            <!-- Group Keys Section -->
            <v-col sm="12" v-if="selectedRewardOption">
              <template v-if="groupKeys.length > 0">
                <v-card
                  v-for="(group, groupIndex) in groupKeys"
                  :key="`group_${groupIndex}`"
                  class="mb-6"
                  outlined
                >
                  <v-card-title class="py-3 d-flex justify-space-between align-center">
                    <span class="subtitle-1">{{ getGroupKeyLabel() }} #{{ groupIndex + 1 }}</span>
                    <v-btn icon small color="error" @click="removeGroupKey(groupIndex)">
                      <v-icon>mdi-close-circle</v-icon>
                    </v-btn>
                  </v-card-title>

                  <v-card-text>
                    <!-- Group Key Fields -->
                    <v-row>
                      <!-- Target Reward & Agency Target Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'target_reward' || selectedRewardOption === 'agency_target_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.target"
                          outlined
                          dense
                          label="Target *"
                          type="number"
                          prepend-inner-icon="mdi-target"
                          :rules="[(v) => !!v || 'Target is required']"
                        />
                      </v-col>

                      <!-- Active Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'active_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.time"
                          outlined
                          dense
                          label="Time (minutes) *"
                          type="number"
                          prepend-inner-icon="mdi-clock"
                          :rules="[(v) => !!v || 'Time is required']"
                        />
                      </v-col>

                      <!-- Invitation Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'invitation_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.no_of_hosts"
                          outlined
                          dense
                          label="Number of Hosts *"
                          type="number"
                          prepend-inner-icon="mdi-account-multiple"
                          :rules="[(v) => !!v || 'Number of hosts is required']"
                        />
                      </v-col>

                      <!-- Daily Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'daily_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.daily_target"
                          outlined
                          dense
                          label="Daily Target *"
                          type="number"
                          prepend-inner-icon="mdi-calendar-today"
                          :rules="[(v) => !!v || 'Daily target is required']"
                        />
                      </v-col>

                      <!-- Monthly Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'monthly_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.monthly_target"
                          outlined
                          dense
                          label="Monthly Target *"
                          type="number"
                          prepend-inner-icon="mdi-calendar-month"
                          :rules="[(v) => !!v || 'Monthly target is required']"
                        />
                      </v-col>

                      <!-- Ranking Reward -->
                      <v-col sm="4" v-if="selectedRewardOption === 'ranking_reward'">
                        <v-text-field
                          v-model.number="group.groupKey.rank_position"
                          outlined
                          dense
                          label="Rank Position *"
                          type="number"
                          prepend-inner-icon="mdi-medal"
                          :rules="[(v) => !!v || 'Rank position is required']"
                        />
                      </v-col>

                      <!-- Performance Reward -->
                      <template v-if="selectedRewardOption === 'performance_reward'">
                        <v-col sm="4">
                          <v-text-field
                            v-model="group.groupKey.performance_metric"
                            outlined
                            dense
                            label="Performance Metric *"
                            prepend-inner-icon="mdi-chart-line"
                            :rules="[(v) => !!v || 'Performance metric is required']"
                          />
                        </v-col>
                        <v-col sm="4">
                          <v-text-field
                            v-model.number="group.groupKey.performance_target"
                            outlined
                            dense
                            label="Target *"
                            type="number"
                            prepend-inner-icon="mdi-target"
                            :rules="[(v) => !!v || 'Target is required']"
                          />
                        </v-col>
                      </template>
                    </v-row>

                    <v-divider class="my-3"></v-divider>

                    <!-- Reward Entries for this Group Key -->
                    <h4 class="mb-3">Reward Entries</h4>

                    <template v-if="group.rewardEntries.length > 0">
                      <v-row
                        v-for="(entry, entryIndex) in group.rewardEntries"
                        :key="`entry_${groupIndex}_${entryIndex}`"
                        class="mb-2"
                      >
                        <v-col sm="3" v-if="!entry.store_item_id || entry.store_item_id === ''">
                          <v-text-field
                            v-model.number="entry.reward_value"
                            outlined
                            dense
                            label="Reward Value"
                            type="number"
                            prepend-inner-icon="mdi-bitcoin"
                            @input="onRewardValueChange(entry)"
                          />
                        </v-col>

                        <v-col sm="3" v-if="!entry.reward_value || entry.reward_value === '' || entry.reward_value === 0">
                          <v-autocomplete
                            v-model="entry.store_item_id"
                            :items="storeItems"
                            item-text="name"
                            item-value="id"
                            outlined
                            dense
                            label="Store Item"
                            prepend-inner-icon="mdi-package-variant"
                            clearable
                            @change="onStoreItemChange(entry)"
                          />
                        </v-col>

                        <v-col sm="3" v-if="entry.store_item_id">
                          <v-text-field
                            v-model="entry.expiry_date"
                            outlined
                            dense
                            label="Expiry Date"
                            type="date"
                            prepend-inner-icon="mdi-calendar-clock"
                            :min="minDate"
                            required
                          />
                        </v-col>

                        <v-col sm="1" class="d-flex align-center">
                          <v-btn icon small color="error" @click="removeRewardEntry(groupIndex, entryIndex)">
                            <v-icon>mdi-delete</v-icon>
                          </v-btn>
                        </v-col>
                      </v-row>
                    </template>

                    <v-row>
                      <v-col>
                        <v-btn color="primary" dark small @click="addRewardEntry(groupIndex)">
                          <v-icon left>mdi-plus</v-icon> Add Reward Entry
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </template>

              <!-- Add Group Key Button -->
              <v-row>
                <v-col>
                  <v-btn color="green" dark @click="addGroupKey">
                    <v-icon left>mdi-plus-circle</v-icon> Add {{ getGroupKeyLabel() }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-divider class="my-5"> </v-divider>
          <v-btn
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
          >
            Create Event
          </v-btn>
          <v-btn rounded class="ml-3" @click="$router.back()"> Cancel </v-btn>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>
<script>
import axios from "axios";
export default {
  name: "EventsCreatePage",
  data() {
    return {
      form: {
        name: "",
        type: "",
        start_date: "",
        end_date: "",
        is_active: true,
      },
      groupKeys: [], // Array of { groupKey: {...}, rewardEntries: [...] }
      storeItems: [],
      eventTypes: [
        { value: "host", text: "Host Event" },
        { value: "agent", text: "Agent Event" },
        { value: "recharge", text: "Recharge Event" },
        { value: "weekly_star", text: "Weekly Star Event" },
      ],
      rewardOptions: {
        host: [
          { value: "target_reward", text: "Target Reward" },
          { value: "active_reward", text: "Active Reward" },
        ],
        agent: [
          { value: "agency_target_reward", text: "Agency Target Reward" },
          { value: "invitation_reward", text: "Invitation Reward" },
        ],
        recharge: [
          { value: "daily_reward", text: "Daily Reward" },
          { value: "monthly_reward", text: "Monthly Reward" },
        ],
        weekly_star: [
          { value: "ranking_reward", text: "Ranking Reward" },
          { value: "performance_reward", text: "Performance Reward" },
        ],
      },
      selectedRewardOption: "",
      isLoading: false,
      image_path: "",
      menu_start: false,
      menu_end: false,
    };
  },
  computed: {
    minDate() {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      return tomorrow.toISOString().split("T")[0];
    },
    availableRewardOptions() {
      if (!this.form.type) return [];
      return this.rewardOptions[this.form.type] || [];
    },
  },
  mounted() {
    this.getStoreItems();
  },
  methods: {
    onEventTypeChange() {
      // Reset reward option and group keys when event type changes
      this.selectedRewardOption = "";
      this.groupKeys = [];
    },
    getGroupKeyLabel() {
      const labels = {
        target_reward: "Target Reward",
        active_reward: "Active Reward",
        agency_target_reward: "Agency Target Reward",
        invitation_reward: "Invitation Reward",
        daily_reward: "Daily Reward",
        monthly_reward: "Monthly Reward",
        ranking_reward: "Ranking Reward",
        performance_reward: "Performance Reward",
      };
      return labels[this.selectedRewardOption] || "Group";
    },
    addGroupKey() {
      this.groupKeys.push({
        groupKey: {
          target: null,
          time: null,
          no_of_hosts: null,
          daily_target: null,
          monthly_target: null,
          rank_position: null,
          performance_metric: "",
          performance_target: null,
        },
        rewardEntries: [],
      });
    },
    removeGroupKey(groupIndex) {
      this.groupKeys.splice(groupIndex, 1);
    },
    addRewardEntry(groupIndex) {
      this.groupKeys[groupIndex].rewardEntries.push({
        reward_value: null,
        store_item_id: null,
        expiry_date: "",
      });
    },
    removeRewardEntry(groupIndex, entryIndex) {
      this.groupKeys[groupIndex].rewardEntries.splice(entryIndex, 1);
    },
    onRewardValueChange(entry) {
      // Clear store_item_id when reward value is entered
      if (entry.reward_value && entry.reward_value !== "" && entry.reward_value !== 0) {
        entry.store_item_id = null;
        entry.expiry_date = "";
      }
    },
    onStoreItemChange(entry) {
      // Clear reward value when store item is selected
      if (entry.store_item_id) {
        entry.reward_value = null;
        // Set default expiry date to tomorrow if not already set
        if (!entry.expiry_date) {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          entry.expiry_date = tomorrow.toISOString().split('T')[0];
        }
      }
    },
    async getStoreItems() {
      try {
        const response = await axios.get("/masters?context=reward_category");
        this.storeItems = response.data.storeItem || [];
      } catch (e) {
        console.error("Error loading store items:", e);
      }
    },
    async save() {
      try {
        if (!this.validateForm()) {
          this.$toast?.error("Please fill all required fields") ||
            alert("Please fill all required fields");
          return;
        }

        // Validate that reward option is selected
        if (!this.selectedRewardOption) {
          this.$toast?.error("Please select a reward option") ||
            alert("Please select a reward option");
          return;
        }

        // Validate that at least one group key exists
        if (this.groupKeys.length === 0) {
          this.$toast?.error("Please add at least one group key configuration") ||
            alert("Please add at least one group key configuration");
          return;
        }

        // Build all rewards from all group keys
        const allRewards = [];

        // Validate each group key and its entries
        for (let groupIndex = 0; groupIndex < this.groupKeys.length; groupIndex++) {
          const group = this.groupKeys[groupIndex];

          // Validate group key fields
          const validationResult = this.validateGroupKey(group.groupKey);
          if (!validationResult.valid) {
            this.$toast?.error(`Group ${groupIndex + 1}: ${validationResult.message}`) ||
              alert(`Group ${groupIndex + 1}: ${validationResult.message}`);
            return;
          }

          // Validate that group has at least one reward entry
          if (group.rewardEntries.length === 0) {
            this.$toast?.error(`Group ${groupIndex + 1} must have at least one reward entry`) ||
              alert(`Group ${groupIndex + 1} must have at least one reward entry`);
            return;
          }

          // Build requirement_json for this group
          const requirement_json = this.buildRequirementJson(group.groupKey);

          // Validate each reward entry in this group
          for (let entryIndex = 0; entryIndex < group.rewardEntries.length; entryIndex++) {
            const entry = group.rewardEntries[entryIndex];

            // Check if entry has either reward_value or store_item_id
            if (!entry.reward_value && !entry.store_item_id) {
              this.$toast?.error(`Group ${groupIndex + 1}, Entry ${entryIndex + 1}: Must have either a reward value or a store item`) ||
                alert(`Group ${groupIndex + 1}, Entry ${entryIndex + 1}: Must have either a reward value or a store item`);
              return;
            }

            // Validate expiry date if store item is selected
            if (entry.store_item_id && entry.expiry_date) {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              const expiryDate = new Date(entry.expiry_date);
              expiryDate.setHours(0, 0, 0, 0);

              if (expiryDate <= today) {
                this.$toast?.error(`Group ${groupIndex + 1}, Entry ${entryIndex + 1}: Expiry date must be after today`) ||
                  alert(`Group ${groupIndex + 1}, Entry ${entryIndex + 1}: Expiry date must be after today`);
                return;
              }
            }

            // Add reward to the list
            allRewards.push({
              reward_type: this.selectedRewardOption,
              requirement_json: requirement_json,
              reward_value: entry.reward_value || null,
              store_item_id: entry.store_item_id || null,
              expiry_date: entry.expiry_date || null,
            });
          }
        }

        this.isLoading = true;

        // Prepare data - send event and all rewards together
        const requestData = {
          ...this.form,
          rewards: allRewards,
        };

        // Send single request with both event and rewards
        let response = await axios.post("/events", requestData);

        if (response.data.success) {
          await this.saveUserTimestamp?.(null, this.form);
          this.$toast?.success("Event and rewards created successfully!") ||
            alert("Event and rewards created successfully!");
          this.$router.push("/events");
        } else {
          throw new Error(response.data.message || "Error creating event");
        }
      } catch (error) {
        console.error("Error creating event:", error);

        // Display specific error message
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Error creating event. Please check all fields and try again.";

        this.$toast?.error(errorMessage) || alert(errorMessage);

        // Display validation errors if any
        if (error.response?.data?.errors) {
          const errors = error.response.data.errors;
          Object.keys(errors).forEach((key) => {
            const errorMsg = Array.isArray(errors[key])
              ? errors[key][0]
              : errors[key];
            this.$toast?.error(errorMsg) || console.error(errorMsg);
          });
        }
      } finally {
        this.isLoading = false;
      }
    },
    buildRequirementJson(groupKey) {
      const requirement_json = {};

      if (this.selectedRewardOption === 'target_reward' || this.selectedRewardOption === 'agency_target_reward') {
        requirement_json.target = groupKey.target;
      } else if (this.selectedRewardOption === 'active_reward') {
        requirement_json.time = groupKey.time;
      } else if (this.selectedRewardOption === 'invitation_reward') {
        requirement_json.no_of_hosts = groupKey.no_of_hosts;
      } else if (this.selectedRewardOption === 'daily_reward') {
        requirement_json.daily_target = groupKey.daily_target;
      } else if (this.selectedRewardOption === 'monthly_reward') {
        requirement_json.monthly_target = groupKey.monthly_target;
      } else if (this.selectedRewardOption === 'ranking_reward') {
        requirement_json.rank_position = groupKey.rank_position;
      } else if (this.selectedRewardOption === 'performance_reward') {
        requirement_json.performance_metric = groupKey.performance_metric;
        requirement_json.performance_target = groupKey.performance_target;
      }

      return requirement_json;
    },
    validateGroupKey(groupKey) {
      if (this.selectedRewardOption === 'target_reward' || this.selectedRewardOption === 'agency_target_reward') {
        return groupKey.target ? { valid: true } : { valid: false, message: "Target is required" };
      } else if (this.selectedRewardOption === 'active_reward') {
        return groupKey.time ? { valid: true } : { valid: false, message: "Time is required" };
      } else if (this.selectedRewardOption === 'invitation_reward') {
        return groupKey.no_of_hosts ? { valid: true } : { valid: false, message: "Number of hosts is required" };
      } else if (this.selectedRewardOption === 'daily_reward') {
        return groupKey.daily_target ? { valid: true } : { valid: false, message: "Daily target is required" };
      } else if (this.selectedRewardOption === 'monthly_reward') {
        return groupKey.monthly_target ? { valid: true } : { valid: false, message: "Monthly target is required" };
      } else if (this.selectedRewardOption === 'ranking_reward') {
        return groupKey.rank_position ? { valid: true } : { valid: false, message: "Rank position is required" };
      } else if (this.selectedRewardOption === 'performance_reward') {
        if (!groupKey.performance_metric) {
          return { valid: false, message: "Performance metric is required" };
        }
        if (!groupKey.performance_target) {
          return { valid: false, message: "Performance target is required" };
        }
        return { valid: true };
      }
      return { valid: false, message: "Invalid group key configuration" };
    },
    validateForm() {
      return !!(
        this.form.name &&
        this.form.type &&
        this.form.start_date &&
        this.form.end_date
      );
    },
  },
};
</script>
